/**
 * New meeting — `docs/activity.md` §9.6 (Board 11 F with a customer, Board
 * 20 A for several people with nothing attached).
 *
 * In the boards' order: **Site visit · Meeting** · *Title* · **Who's going**
 * (one person or several, up to 25 — §4.7) · *When* (the day, the start, the
 * length) and *Repeats* · the day under it — one person's day with the drive
 * to the new place, or everyone's in the time finder · **Attached to**
 * (optional) · *Where* | *With* · *Text <first name> a confirmation*.
 *
 * Aaron, Oct 23 (note 4): one search fills *Where* and *With* at once;
 * *Where* offers the property (attached or searched), the branch office, an
 * address (Google's lookup) or a call; *With* searches the same way; and
 * *Repeats* has Outlook's options behind **Custom…** (`recurrence-panel.tsx`).
 *
 * Aaron, Oct 24 (note 1.1): that search **is** *Attached to* now — the one
 * box above *Where* | *With*, the chips inside it, nothing at the bottom. It
 * finds jobs, visits and invoices too; a place picked is *Where* and its
 * contact *With*, a job or visit brings its place and contact the same way, a
 * contact with several places offers them in *Where* (small question 1), and
 * an address that is not on file can be made a property or just used. And a
 * meeting reopened reads back what it was saved with — *Attached to*, *With*,
 * *Where* — and saves all of it again (note 1.1.6: "it didn't retain any of
 * it"; note 1.4).
 *
 * Aaron, Oct 27 (answer 12: *Just this one* "should be able to change pretty
 * much anything"): opened for one occurrence of a repeating meeting
 * (`prefill.occurrence`), it is that occurrence — its own day, time, title,
 * people, place, *With*, *Attached to* and notes, read from
 * `GET /activity/meetings/:id?occurrence=` — and **Save** writes them for it
 * alone (`POST /activity/occurrences/:id/edit`); *Repeats* is the series' and
 * is said, not offered. Reopened to edit, a meeting has its *Notes* too.
 *
 * One meeting for several people is one row with its participants. A
 * **Site visit** is folder 48's: it needs a job and goes through book mode,
 * so a customer's **C** confirms it exactly as there. A meeting's own
 * confirmation is §19 A2's words, queued against the meeting
 * (`POST /activity/meetings/:id/confirm`) after it is made.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  MEETING_LENGTHS, firstNameOf, goingWords, lengthWords, meetingFooterWords, monthDay, storedRepeat, weekdayShort,
  zonedInstant, zonedLocal, type MeetingFit, type MeetingPlace, type Recurrence,
} from '@rowted/shared'
import { AddressAutocomplete, type ChosenAddress } from '../../components/form/address-autocomplete.js'
import { RichNoteEditor } from '../../components/note-rich-editor.js'
import { requestsApi } from '../../requests/api.js'
import { uuidv7 } from 'uuidv7'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Glyph } from '../glyphs.js'
import {
  AttachedTo, cameWithWords, suggestFor, withChips, type AttachChip, type AttachFound, type AttachType,
} from './attach.js'
import {
  makeActivity, numberWords, openedFromWords, problemOf, refsOf, useContact, usePrefillChips, useSetup,
  type NewPrefill,
} from './common.js'
import { ContactField } from './contact-field.js'
import { meetingContacts } from './meeting-find.js'
import { CustomRepeat, RepeatsMenu } from './recurrence-panel.js'
import { announceMoved } from '../calendar/tell.js'
import { DayStrip, TimeFinder, stripLabel, useFree } from './day-strip.js'
import { Field, Gap, MenuField, NewShell, PICKER_LAYER, Pair, Segments, SmallSwitch, TextBox, boxClass } from './kit.js'
import { PeopleField, usePeopleLookup } from './people-field.js'
import { DayField, TimeField } from './when.js'

type WhereKind = 'property' | 'office' | 'address' | 'remote'

/** A meeting as the sheet edits it (`GET /activity/meetings/:id`, Aaron, Oct 24, note 1.1.6). */
interface MeetingAnswer {
  id: string; kind: 'meeting' | 'appointment'; title: string; date: string; start: number; end: number; people: { id: string }[]
  propertyId: string | null; propertyName: string | null; remote: boolean; recurrence?: Recurrence | null
  /** The occurrence it was read for — its own day in the series — or null for the series (Aaron, Oct 27, answer 12). */
  occurrence?: string | null
  /** *Meeting* · *Every Thursday* (Board 20 C): the series said, under *Repeats* for one occurrence. */
  tags?: string[]
  /** Its notes as saved: the occurrence's own, or the series'. */
  body?: string
  links: AttachChip[]
  withContactId: string | null
  location: {
    kind: WhereKind | null; propertyId: string | null
    address: { line1: string; city?: string; state?: string; postalCode?: string; latitude?: number; longitude?: number } | null
  }
}

/**
 * The chips as they are saved: *With*'s contact attached, and first among the
 * contacts, so the meeting reopened reads it back as *With* (note 1.1.6).
 */
export function meetingLinks(chips: readonly AttachChip[], withContact: { id: string; name: string } | null): AttachChip[] {
  if (!withContact) return [...chips]
  const own = chips.find((chip) => chip.type === 'contact' && chip.id === withContact.id)
    ?? { type: 'contact' as AttachType, id: withContact.id, label: withContact.name, auto: false }
  const rest = chips.filter((chip) => chip !== own)
  const firstContact = rest.findIndex((chip) => chip.type === 'contact')
  const at = chips.includes(own) ? chips.indexOf(own) : rest.length
  const index = firstContact >= 0 && firstContact < at ? firstContact : at
  return [...rest.slice(0, index), own, ...rest.slice(index)]
}

/** The next quarter hour after now, or 9:00 AM on another day. */
function defaultStart(date: string, today: string, nowMinutes: number): number {
  if (date !== today) return 9 * 60
  return Math.min(20 * 60, Math.ceil((nowMinutes + 1) / 15) * 15)
}

export function MeetingSheet({
  prefill, phone, onClose, onMade,
}: { prefill: NewPrefill; phone: boolean; onClose: () => void; onMade: () => void }) {
  const { me, today, change } = useActivity()
  const setup = useSetup()
  const { byId } = usePeopleLookup()
  const timeZone = me.user.timeZone
  const [title, setTitle] = useState(prefill.title ?? '')
  const [people, setPeople] = useState<string[]>(prefill.people?.length ? prefill.people : [me.user.id])
  const [date, setDate] = useState<string>(prefill.date ?? today)
  /* Until the set-up says what time Activity's day is at, a meeting today starts on the hour after nine. */
  const [start, setStart] = useState<number>(prefill.start ?? defaultStart(prefill.date ?? today, today, 9 * 60))
  const startChosen = useRef(prefill.start !== undefined || Boolean(prefill.editId))
  useEffect(() => {
    if (!setup || startChosen.current) return
    /* A server that does not say what time it is leaves the start where it is. */
    const now = setup.now ? new Date(setup.now) : null
    if (!now || Number.isNaN(now.getTime())) return
    startChosen.current = true
    setStart(defaultStart(date, today, zonedLocal(now, timeZone).minutes))
  }, [setup])
  /* *Site visit · Meeting* picks the kind and its colour, nothing else (do-not-substitute 17). */
  const [kind, setKind] = useState<'meeting' | 'site_visit'>('meeting')
  const [minutes, setMinutes] = useState<number>(prefill.minutes ?? 60)
  const [recurrence, setRecurrence] = useState<Recurrence | null>(null)
  const [customOpen, setCustomOpen] = useState(false)
  const findInput = useRef<HTMLInputElement>(null)
  const [chips, setChips] = usePrefillChips(prefill)
  const [picked, setPicked] = useState<{ chip: AttachChip; brought: AttachChip[] } | null>(null)
  /* A contact with several places: *Where* offers them (small question 1). */
  const [choosing, setChoosing] = useState<{ firstName: string } | null>(null)
  const [withId, setWithId] = useState<string | null>(prefill.contact?.id ?? prefill.contactId ?? null)
  const withContact = useContact(withId)
  /* A place picked in the quick popover is *Where* here too (Aaron: More options kept only the office). */
  const [where, setWhere] = useState<{ kind: WhereKind; propertyId?: string } | null>(
    prefill.place ? { kind: 'property', propertyId: prefill.place.id } : null)
  /* Places found by the search, offered in *Where* even when taken off *Attached to*. */
  const [found, setFound] = useState<{ id: string; label: string }[]>(
    prefill.place ? [{ id: prefill.place.id, label: prefill.place.label }] : [])
  const [address, setAddress] = useState<ChosenAddress | null>(null)
  const [addressLine, setAddressLine] = useState('')
  const [confirm, setConfirm] = useState(true)
  const [fit, setFit] = useState<MeetingFit | null>(null)
  const [places, setPlaces] = useState<Record<string, MeetingPlace>>({})
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  /* A site visit moved here (one with no job to book it through) keeps its place and its links: it is the job's. */
  const [editingVisit, setEditingVisit] = useState(false)
  /* *Edit › Just this one*: the occurrence it is, by its own day in the series, and the series said (answer 12). */
  const [occurrence, setOccurrence] = useState<string | null>(null)
  const [seriesWords, setSeriesWords] = useState<string | null>(null)
  /*
   * Its notes; sent only once somebody has written in them, so Save on what
   * was shown rewrites nothing. The box says it changed as it comes up (it
   * re-reads itself), so a change counts only while the focus is in it.
   */
  const [notes, setNotes] = useState('')
  const notesTouched = useRef(false)
  const notesBox = useRef<HTMLDivElement>(null)
  const threadContact = useContact(prefill.fromConversationId ? prefill.contactId ?? null : null)

  /* Where an edited meeting was when it was opened, to know whether it was moved. */
  const movedFrom = useRef<{ date: string; start: number } | null>(null)
  /*
   * *Reschedule* / *Move* / a double-click: the meeting as it is, in the same
   * sheet (§6.5) — its time and people, and what it is attached to, who it is
   * with and where it is (Aaron, Oct 24, note 1.1.6: reopened, it showed none
   * of those, and a save sent none of them).
   */
  useEffect(() => {
    if (!prefill.editId) return undefined
    let live = true
    const one = prefill.occurrence ? `?occurrence=${prefill.occurrence}` : ''
    activityRequest<MeetingAnswer>(`/activity/meetings/${prefill.editId}${one}`)
      .then((card) => {
        if (!live) return
        setEditing(card.id)
        setOccurrence(prefill.occurrence && card.occurrence === prefill.occurrence ? card.occurrence : null)
        setSeriesWords(card.tags?.find((tag) => tag.startsWith('Every ')) ?? null)
        setNotes(card.body ?? '')
        setEditingVisit(card.kind === 'appointment')
        setTitle(card.title)
        setDate(card.date)
        setStart(card.start)
        movedFrom.current = { date: card.date, start: card.start }
        setMinutes(Math.max(15, card.end - card.start))
        if (card.people.length > 0) setPeople(card.people.map((person) => person.id))
        setChips(card.links)
        setWithId(card.withContactId)
        const at = card.location
        if (at.kind === 'property' && at.propertyId) {
          setWhere({ kind: 'property', propertyId: at.propertyId })
          const label = card.links.find((chip) => chip.type === 'property' && chip.id === at.propertyId)?.label
            ?? card.propertyName ?? ''
          setFound((was) => (was.some((one) => one.id === at.propertyId) ? was : [...was, { id: at.propertyId!, label }]))
        } else if (at.kind === 'address' && at.address) {
          setWhere({ kind: 'address' })
          setAddress({
            line1: at.address.line1, city: at.address.city ?? '', state: at.address.state ?? '',
            postalCode: at.address.postalCode ?? '', latitude: at.address.latitude ?? null, longitude: at.address.longitude ?? null,
          })
          setAddressLine(at.address.line1)
        } else {
          setWhere({ kind: at.kind === 'remote' ? 'remote' : 'office' })
        }
        /* Moving a repeating meeting keeps its repeat: the sheet starts from it rather than from *Never*. */
        setRecurrence(card.recurrence ?? null)
      })
      .catch(() => { if (live) setProblem('That meeting could not be opened.') })
    return () => { live = false }
  }, [prefill.editId, prefill.occurrence])

  /* *Where* follows the first attached property until somebody picks one (§9.6). */
  const firstProperty = chips.find((chip) => chip.type === 'property') ?? null
  const place: { kind: WhereKind; propertyId?: string } = where
    ?? (firstProperty ? { kind: 'property', propertyId: firstProperty.id } : { kind: 'office' })
  const propertyIds = useMemo(() => [...new Set([
    ...chips.filter((chip) => chip.type === 'property').map((chip) => chip.id),
    ...found.map((one) => one.id),
    ...(place.propertyId ? [place.propertyId] : []),
  ])], [chips, found, place.propertyId])
  useEffect(() => {
    for (const id of propertyIds) {
      if (places[id]) continue
      activityRequest<MeetingPlace>(`/activity/place?propertyId=${id}`)
        .then((found) => setPlaces((was) => ({ ...was, [id]: found })))
        .catch(() => {})
    }
  }, [propertyIds, places])

  const office = setup?.office ?? null
  const placeCity = (() => {
    if (place.kind === 'property' && place.propertyId) {
      const found = places[place.propertyId]
      return found?.city ? `${found.city}${found.state ? `, ${found.state}` : ''}` : null
    }
    if (place.kind === 'office' && office?.city) return `${office.city}${office.state ? `, ${office.state}` : ''}`
    if (place.kind === 'address' && address?.city) return `${address.city}${address.state ? `, ${address.state}` : ''}`
    return null
  })()

  /* The people's day and whether this time works for them (the server's answers, §9.6). */
  const free = useFree(people, date, minutes, start + 15, activityRequest)
  const fitKey = `${people.join(',')}|${date}|${start}|${minutes}|${place.kind}|${place.propertyId ?? ''}|${address?.latitude ?? ''}`
  useEffect(() => {
    if (people.length === 0) { setFit(null); return undefined }
    let live = true
    const query = new URLSearchParams({ people: people.join(','), date, start: String(start), minutes: String(minutes) })
    if (place.kind === 'property' && place.propertyId) query.set('propertyId', place.propertyId)
    if (place.kind === 'office' && office) query.set('orgUnitId', office.orgUnitId)
    if (place.kind === 'address' && address?.latitude != null && address.longitude != null) {
      query.set('lat', String(address.latitude))
      query.set('lng', String(address.longitude))
    }
    if (editing) query.set('except', editing)
    const timer = window.setTimeout(() => {
      activityRequest<MeetingFit>(`/activity/meeting-fit?${query.toString()}`)
        .then((answer) => { if (live) setFit(answer) })
        .catch(() => { if (live) setFit(null) })
    }, 150)
    return () => { live = false; window.clearTimeout(timer) }
  }, [fitKey, office?.orgUnitId, editing])

  const going = people.map((id) => ({
    id,
    firstName: free?.people.find((person) => person.id === id)?.firstName
      ?? (byId(id)?.name ?? (id === me.user.id ? me.user.name : '')).split(/\s+/)[0] ?? '',
    free: fit?.free[id] ?? true,
  }))
  const firstNames = going.map((person) => person.firstName)
  const job = chips.find((chip) => chip.type === 'job') ?? null

  /*
   * What a pick in *Attached to* implies (Aaron, Oct 24, note 1.1.3): a place
   * — picked, or a job's, a visit's, a contact's only one — is *Where*, and
   * its contact *With*; a contact's several places are offered in *Where*
   * (small question 1); an address that is not on file, used as it is, is
   * *Where* too (note 1.1.4). The chips themselves are `AttachedTo`'s.
   */
  const addFound = (place: { id: string; name: string }) =>
    setFound((was) => (was.some((one) => one.id === place.id) ? was : [...was, { id: place.id, label: place.name }]))
  const onFound = (pick: AttachFound) => {
    setChoosing(null)
    if (pick.address) {
      setWhere({ kind: 'address' })
      setAddress(pick.address)
      setAddressLine(pick.address.line1)
      return
    }
    if (pick.property) {
      addFound(pick.property)
      setWhere({ kind: 'property', propertyId: pick.property.id })
    }
    for (const place of pick.places ?? []) addFound(place)
    if (pick.contact) {
      setWithId(pick.contact.id)
      setConfirm(true)
      if (pick.places && pick.places.length > 1 && !pick.property) {
        setChoosing({ firstName: firstNameOf(pick.contact.name) })
      }
    }
  }

  const pickWith = async (id: string) => {
    setWithId(id)
    setConfirm(true)
    setChoosing(null)
    if (chips.some((chip) => chip.type === 'contact' && chip.id === id)) return
    const label = await activityRequest<{ name: string }>(`/activity/compose?contactId=${id}`).then((one) => one.name).catch(() => '')
    const chip: AttachChip = { type: 'contact', id, label, auto: false }
    const brought = (await suggestFor('contact', id)).filter((one) => !chips.some((had) => had.type === one.type && had.id === one.id))
    setChips(withChips([...chips, chip], brought))
    setPicked({ chip, brought })
  }

  /*
   * A site visit is the job's, booked through folder 48's own server function
   * (§9.6), so it is confirmed, and its *C* handled, exactly as there.
   */
  const bookVisit = async () => {
    if (!job) { setProblem('Attach a job to book a site visit'); return }
    if (people.length !== 1) { setProblem('A site visit is for one person.'); return }
    setBusy(true)
    setProblem(null)
    try {
      await requestsApi.book({
        requestId: uuidv7(), jobId: job.id, appointmentId: null, salespersonId: people[0]!,
        startsAt: zonedInstant(date, start, timeZone).toISOString(), minutes: Math.min(480, Math.max(15, minutes)),
        description: title.trim(),
        confirm: confirm && withContact?.mobile && !withContact.optedOut ? { channel: 'sms', alsoTo: [] } : null,
      })
      onMade()
      onClose()
    } catch (cause) {
      setProblem(problemOf(cause))
    } finally {
      setBusy(false)
    }
  }

  const create = async () => {
    if (!title.trim()) { setProblem('Give the meeting a title.'); return }
    if (people.length === 0) { setProblem('Pick who’s going.'); return }
    if (kind === 'site_visit' && !editing) { await bookVisit(); return }
    if (place.kind === 'address' && !addressLine.trim()) { setProblem('Type the address.'); return }
    setBusy(true)
    setProblem(null)
    const repeatFields = storedRepeat(kind === 'site_visit' ? null : recurrence)
    /* The whole place, every time — a reopened meeting given a new one keeps it (note 1.1.6). */
    const location = place.kind === 'property'
      ? { locationKind: 'property', locationPropertyId: place.propertyId ?? null, locationAddress: null }
      : place.kind === 'address' ? {
        locationKind: 'address',
        locationPropertyId: null,
        locationAddress: {
          line1: address?.line1 ?? addressLine.trim(),
          ...(address?.city ? { city: address.city } : {}),
          ...(address?.state ? { state: address.state } : {}),
          ...(address?.postalCode ? { postalCode: address.postalCode } : {}),
          ...(address?.latitude != null && address.longitude != null ? { latitude: address.latitude, longitude: address.longitude } : {}),
        },
      }
        : { locationKind: place.kind, locationPropertyId: null, locationAddress: null }
    /* What it is attached to, with *With*'s contact on it — a meeting with a customer is on their record too (§9.6). */
    const withPerson = withId
      ? { id: withId, name: withContact?.name ?? chips.find((chip) => chip.type === 'contact' && chip.id === withId)?.label ?? '' }
      : null
    const links = meetingLinks(chips, withPerson)
    /*
     * *Nobody from outside* over a contact that stays a chip is stored as that
     * (Aaron, Oct 26, answer 10; 0150), or the meeting reopens with the chip's
     * contact back in *With*. With no contact on it, nobody is all it can be.
     */
    const withNobody = !withPerson && links.some((chip) => chip.type === 'contact')
    const notesSent = notesTouched.current ? { body: notes } : {}
    const body = {
      kind: 'meeting',
      title: title.trim(),
      dueAt: zonedInstant(date, start, timeZone).toISOString(),
      durationMinutes: minutes,
      participantIds: people,
      ...repeatFields,
      visibility: 'internal',
      ...(office && place.kind === 'office' ? { orgUnitId: office.orgUnitId } : {}),
      ...location,
      ...(withNobody ? { withNobody: true } : {}),
    }
    try {
      let id: string
      if (editing && occurrence) {
        /* Just this one (answer 12): everything the sheet holds, for that occurrence alone. */
        await activityRequest(`/activity/occurrences/${editing}/edit`, {
          method: 'POST',
          body: {
            occurrence, startsAt: body.dueAt, minutes, title: body.title, participantIds: people,
            locationKind: location.locationKind, locationPropertyId: location.locationPropertyId,
            locationAddress: location.locationAddress, links: refsOf(links), withNobody, ...notesSent,
          },
        })
        id = editing
        if (movedFrom.current && (movedFrom.current.date !== date || movedFrom.current.start !== start)) {
          announceMoved({ id: editing, kind: 'meeting', occurrence })
        }
      } else if (editing) {
        await activityRequest(`/activities/${editing}`, {
          method: 'PATCH',
          body: {
            title: body.title, dueAt: body.dueAt, durationMinutes: minutes, participantIds: people, ...repeatFields,
            ...(editingVisit ? {} : { ...location, links: refsOf(links), withNobody, ...notesSent }),
          },
        })
        id = editing
        /* A new time: the calendar offers to tell the customer (Aaron, Oct 24, note 1.8). */
        if (movedFrom.current && (movedFrom.current.date !== date || movedFrom.current.start !== start)) {
          announceMoved({ id: editing, kind: 'meeting' })
        }
      } else {
        id = (await makeActivity(body, links)).id
      }
      if (!editing && confirm && withContact?.mobile && !withContact.optedOut) {
        try {
          await activityRequest(`/activity/meetings/${id}/confirm`, { method: 'POST', body: { contactId: withContact.id } })
        } catch (cause) {
          setProblem(`The meeting is made; the text didn’t go: ${problemOf(cause)}`)
          onMade()
          return
        }
      }
      onMade()
      onClose()
    } catch (cause) {
      setProblem(problemOf(cause))
    } finally {
      setBusy(false)
    }
  }

  const offeredPlaces = [
    ...chips.filter((chip) => chip.type === 'property').map((chip) => ({ id: chip.id, label: chip.label })),
    ...found.filter((one) => !chips.some((chip) => chip.type === 'property' && chip.id === one.id)),
  ]
  const whereOptions = [
    ...offeredPlaces.map((chip) => {
      const known = places[chip.id]
      return {
        value: `property:${chip.id}`, label: known?.line1 ?? chip.label,
        after: known?.city ? `${known.city}${known.state ? `, ${known.state}` : ''}` : undefined,
        lead: <span className="text-text-muted"><Glyph name="pin" size={14} /></span>,
      }
    }),
    ...(office ? [{
      value: 'office', label: office.name, after: office.city ? `${office.city}${office.state ? `, ${office.state}` : ''}` : undefined,
      lead: <span className="text-text-muted"><Glyph name="pin" size={14} /></span>,
    }] : []),
    { value: 'find', label: 'A property…', sub: 'find one by name, street or its contact',
      lead: <span className="text-text-muted"><Glyph name="search" size={14} /></span> },
    { value: 'address', label: 'An address…', lead: <span className="text-text-muted"><Glyph name="house" size={14} /></span> },
    { value: 'remote', label: 'By phone or video', lead: <span className="text-text-muted"><Glyph name="phone" size={14} /></span> },
  ]
  const whereValue = place.kind === 'property' ? `property:${place.propertyId}` : place.kind
  const whereHint = choosing ? `${choosing.firstName} has more than one property — pick one, or an address`
    : place.kind === 'property' ? 'the property · or the office, an address, by phone or video'
    : place.kind === 'office' ? 'the branch office · or a property, an address, by phone or video'
      : place.kind === 'address' ? 'an address · or the office, a property, by phone or video'
        : 'by phone or video · or the office, a property, an address'
  const whereShown = (() => {
    if (place.kind === 'property' && place.propertyId) {
      const known = places[place.propertyId]
      const chip = offeredPlaces.find((one) => one.id === place.propertyId)
      return { bold: known?.line1 ?? chip?.label ?? prefill.place?.label ?? 'The property', after: placeCity }
    }
    if (place.kind === 'office') return { bold: office?.name ?? 'The office', after: placeCity }
    if (place.kind === 'address') return { bold: address?.line1 || addressLine.trim() || 'An address', after: placeCity }
    return { bold: 'By phone or video', after: null }
  })()

  const attachedHint = (() => {
    if (chips.length === 0) {
      const where = people.length > 1 ? `the ${numberWords(people.length)} calendars` : `${firstNames[0] ?? 'your'}’s calendar`
      return `nothing attached: it shows on ${where} and in All activity, not on any record`
    }
    return cameWithWords(picked) ?? openedFromWords({ name: threadContact?.name ?? null, kind: 'thread', required: false, phone })
  })()

  const label = stripLabel(start, withContact?.firstName ?? title.trim().split(/\s+/)[0] ?? '')
  const openCalendar = () => {
    change({ tab: 'calendar', calendar: { date, ...(people.length > 1 ? { viewSeveral: 'day' } : { viewOne: 'day' }) } })
  }

  /*
   * A repeating meeting opened to edit is its series (*Every one*): *When* is
   * the first one, which every other follows — said, because it was opened
   * from one of the others (Aaron, Oct 25, phone note 4). Opened for one
   * occurrence (*Just this one*), *When* is that one's, and only it moves.
   */
  const oneDay = movedFrom.current?.date ?? date
  const whenHint = occurrence ? `${weekdayShort(oneDay)}, ${monthDay(oneDay)} only — the rest stay as they are`
    : editing && recurrence ? 'the first one · every one follows it' : undefined
  /* One occurrence repeats as its series does: said, not offered. */
  const repeatsField = (
    <Field label="Repeats" phone={phone}>
      {occurrence ? (
        <div className={boxClass(phone)} data-series-repeats>
          <span className="truncate text-text-muted">{seriesWords ? `${seriesWords} · this one only` : 'This one only'}</span>
        </div>
      ) : (
        <RepeatsMenu date={date} value={recurrence} phone={phone}
          onChange={(next) => { setRecurrence(next); setCustomOpen(false) }} onCustom={() => setCustomOpen(true)} />
      )}
    </Field>
  )
  const whenField = (
    <Field label="When" required phone={phone} hint={whenHint}>
      <div className="flex items-center gap-1.5">
        <DayField label="Day" value={date} onChange={setDate} phone={phone} today={today} />
        <TimeField label="Starts" value={start} onChange={(next) => { startChosen.current = true; setStart(next ?? start) }}
          phone={phone} width={phone ? undefined : 86} />
        <MenuField label="Length" phone={phone} width={phone ? undefined : 56} value={String(minutes)}
          /* Its own length too, when it is none of them — a phone's edit mode makes any quarter hour (Aaron, Oct 27, answer 6). */
          options={[...new Set([...MEETING_LENGTHS, minutes])].sort((a, b) => a - b).map((one) => ({ value: String(one), label: lengthWords(one) }))}
          onChange={(next) => setMinutes(Number(next))} />
      </div>
    </Field>
  )

  return (
    <NewShell title={editing ? 'Meeting' : 'New meeting'} sub={setup?.sub ?? ''} look={kind === 'site_visit' ? 'visit' : 'meeting'}
      width={620} phone={phone}
      note={meetingFooterWords({ firstNames, date, start, minutes, repeatRule: null,
        recurrence: kind === 'site_visit' || occurrence ? null : recurrence })}
      primary={editing ? 'Save meeting' : kind === 'site_visit' ? 'Book' : 'Create meeting'}
      phoneAction={editing ? 'Save' : kind === 'site_visit' ? 'Book' : 'Create'}
      onPrimary={() => void create()} busy={busy}
      dirty={!editing && title.trim().length > 0} onClose={onClose} problem={problem}>
      {!editing && (
        <>
          <div className="flex items-center gap-2.5">
            <Segments label="Kind" value={kind} height={phone ? 36 : 24}
              options={[
                { value: 'site_visit', label: 'Site visit', title: job ? undefined : 'Attach a job to book a site visit' },
                { value: 'meeting', label: 'Meeting' },
              ]}
              onChange={(next) => {
                if (next === 'meeting') { setKind('meeting'); setProblem(null); return }
                if (!job) { setProblem('Attach a job to book a site visit'); return }
                if (setup && !setup.jobsEdit) { setProblem('Booking a site visit needs edit access to jobs.'); return }
                setProblem(null)
                setKind('site_visit')
              }} />
            <span className="text-[11.5px] text-text-faint">kinds with their own fields come later</span>
          </div>
          <Gap />
        </>
      )}
      <Field label="Title" required phone={phone}>
        <TextBox label="Title" value={title} onChange={setTitle} phone={phone} autoFocus bold={!phone} />
      </Field>
      <Gap />
      <PeopleField label="Who’s going" required people={people} onChange={setPeople} phone={phone}
        words={goingWords(going)} />
      <Gap />
      {phone ? (
        <>
          {whenField}
          <Gap />
          {repeatsField}
        </>
      ) : (
        <Pair>
          {whenField}
          {repeatsField}
        </Pair>
      )}
      {customOpen && !occurrence && (
        <>
          <Gap size={8} />
          <CustomRepeat date={date} value={recurrence} onChange={setRecurrence} onDone={() => setCustomOpen(false)}
            phone={phone} today={today} />
        </>
      )}
      <Gap />
      {people.length > 1 ? (
        <TimeFinder people={people.map((id) => free?.people.find((person) => person.id === id)).filter((one): one is NonNullable<typeof one> => Boolean(one))}
          date={date} start={start} minutes={minutes} free={fit?.free ?? {}}
          nextAvailable={Boolean(free?.next)}
          onNext={() => { if (free?.next) { setDate(free.next.date); setStart(free.next.start) } }} />
      ) : (
        <DayStrip person={free?.people[0]} date={date} start={start} minutes={minutes} label={label}
          city={placeCity} fit={fit} onOpenCalendar={openCalendar} />
      )}
      <Gap />
      <AttachedTo chips={chips} required={false} phone={phone} hint={attachedHint} id="new-meeting-attached"
        inputRef={findInput} useAddress onFound={onFound}
        onChange={(next, pick) => { setChips(next); setPicked(pick ?? null) }} />
      <Gap />
      <div className={phone ? 'flex flex-col gap-3' : 'grid grid-cols-2 gap-x-3.5 gap-y-3'}>
        <Field label="Where" hint={whereHint} phone={phone}>
          <MenuField label="Where" phone={phone} value={whereValue} options={whereOptions}
            onChange={(next) => {
              if (next === 'find') { window.setTimeout(() => findInput.current?.focus(), 60); return }
              setChoosing(null)
              if (next.startsWith('property:')) setWhere({ kind: 'property', propertyId: next.slice(9) })
              else setWhere({ kind: next as WhereKind })
            }}
            display={(
              <>
                <span className="shrink-0 text-text-muted"><Glyph name={place.kind === 'remote' ? 'phone' : 'pin'} size={14} /></span>
                <b className="truncate">{whereShown.bold}</b>
                {whereShown.after && <span className="truncate text-text-muted">{whereShown.after}</span>}
              </>
            )} />
          {place.kind === 'address' && (
            <div className="mt-1.5">
              <AddressAutocomplete value={addressLine} placeholder="Street address"
                onChange={(line) => { setAddressLine(line); setAddress(null) }}
                onChoose={(chosen) => { setAddress(chosen); setAddressLine(chosen.line1) }} />
            </div>
          )}
        </Field>
        <Field label="With" optional phone={phone} hint={withContact ? undefined : 'nobody from outside — a meeting inside the company'}>
          <ContactField label="With" phone={phone} contact={withContact} onPick={(id) => void pickWith(id)}
            onClear={() => setWithId(null)} clearLabel="Nobody from outside" search={meetingContacts} />
        </Field>
      </div>
      {editing && !editingVisit && (
        <>
          <Gap />
          {/* Its notes, reopened (Aaron, Oct 27, answer 12 — one occurrence's own, or the series'). */}
          <Field label="Notes" optional phone={phone}>
            <div ref={notesBox}>
              <RichNoteEditor label="Notes" value={notes} phone={phone} layer={PICKER_LAYER} minHeight={phone ? 92 : 66}
                placeholder={phone ? 'Add a note' : undefined}
                onChange={(html) => {
                  if (notesBox.current?.contains(document.activeElement)) notesTouched.current = true
                  setNotes(html)
                }} />
            </div>
          </Field>
        </>
      )}
      {withContact && !editing && (
        <>
          <Gap />
          <button type="button" role="switch" aria-checked={confirm && Boolean(withContact.mobile) && !withContact.optedOut}
            disabled={!withContact.mobile || withContact.optedOut}
            onClick={() => setConfirm((was) => !was)}
            className="flex items-center gap-2.5 text-left text-[13px] disabled:opacity-60">
            <SmallSwitch on={confirm && Boolean(withContact.mobile) && !withContact.optedOut} />
            Text {withContact.firstName} a confirmation
            <span className="text-[11.5px] text-text-faint">
              {withContact.optedOut ? `${withContact.firstName} has asked not to be texted`
                : !withContact.mobile ? `No mobile number for ${withContact.firstName}`
                  : 'with Reply C to confirm, as for site visits'}
            </span>
          </button>
        </>
      )}
    </NewShell>
  )
}
